import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ApiRequestError, onClientFault, onUnauthorized, setActiveClientId, type ClientFault } from "@/api/client";
import { useMe, useLogout } from "@/api/queries/auth";
import { useBadges } from "@/api/queries/badges";
import { useFreshness } from "@/api/queries/freshness";
import { useKillSwitch, useSetKillSwitch } from "@/api/queries/killSwitch";
import { useSettings } from "@/api/queries/settings";
import { useWhatsAppConversations } from "@/api/queries/whatsapp";
import type { Client, MeResponse } from "@/api/schemas";
import { ErrorState } from "@/components/states/ErrorState";
import { KillSwitchBanner } from "@/components/states/KillSwitchBanner";
import { OfflineBanner } from "@/components/states/OfflineBanner";
import { PageLoading } from "@/components/states/PageLoading";
import { UnsavedChangesGuard } from "@/components/states/UnsavedChangesGuard";
import { CambiosSinGuardarProvider } from "@/components/states/CambiosSinGuardarProvider";
import { useApplyTheme } from "@/hooks/useApplyTheme";
import { useAvisoDeCambioDeCliente } from "@/hooks/useAvisoDeCambioDeCliente";
import { useBarraLateral } from "@/hooks/useBarraLateral";
import { useCambioDeCliente } from "@/hooks/useCambioDeCliente";
import { useClientAccent } from "@/hooks/useClientAccent";
import { useClienteActivo } from "@/hooks/useClienteActivo";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { useRecientes } from "@/hooks/useRecientes";
import { ATAJOS, destinoPorId, rutaDeCliente, seccionDeRuta, type DestinoId } from "@/routes/routeConfig";
import { describeApiError } from "@/utils/apiError";
import { olvidarInvitacionPendiente } from "@/utils/invitacionPendiente";
import { olvidarSesion } from "@/utils/sesion";
import { deriveNavBadges } from "@/utils/navBadges";
import { BottomTabBar } from "./BottomTabBar";
import { BrakeDialog } from "./BrakeDialog";
import { CommandPalette } from "./CommandPalette";
import { MobileHeader } from "./MobileHeader";
import { ShortcutsHelp } from "./ShortcutsHelp";
import { Sidebar, type DestinoDeAgencia } from "./Sidebar";
import styles from "./AppShell.module.css";

type OverlayLayer = "palette" | "help" | "brake" | null;

const NOMBRE_DE_PRODUCTO = "Safent Ads";
const TITULOS_DE_AGENCIA: Record<string, { titulo: string; destino: DestinoDeAgencia }> = {
  "/clientes": { titulo: "Clientes", destino: "clientes" },
  "/clientes/nuevo": { titulo: "Nuevo cliente", destino: "clientes" },
  "/agencia/equipo": { titulo: "Equipo de la agencia", destino: "agencia-equipo" },
};

function entrarConVolver(pathname: string, search: string): string {
  return `/entrar?volver=${encodeURIComponent(`${pathname}${search}`)}`;
}

/** Sesión + marco de navegación. Una agencia sin clientes también entra: llega a `/clientes` para crear el primero. Una sesión caducada lleva a `/entrar?volver=` y vuelve a la misma URL al entrar. */
export function AppShell() {
  const session = useMe();
  const { data: me, isLoading } = session;
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const here = useRef(`${location.pathname}${location.search}`);
  here.current = `${location.pathname}${location.search}`;
  const salidaVoluntaria = useRef(false);

  useEffect(() => onUnauthorized(() => {
    olvidarSesion(queryClient);
    navigate(`/entrar?volver=${encodeURIComponent(here.current)}`, { replace: true });
  }), [navigate, queryClient]);

  if (isLoading) return <PageLoading label="Comprobando tu sesión…" />;
  if (session.isError && !(session.error instanceof ApiRequestError && session.error.status === 401)) {
    return <ErrorState message={describeApiError(session.error)} onRetry={() => void session.refetch()} />;
  }
  if (!me) return <Navigate to={salidaVoluntaria.current ? "/entrar" : entrarConVolver(location.pathname, location.search)} replace />;
  return (
    <CambiosSinGuardarProvider>
      <Marco key={me.principal.user_id} me={me} onSalir={() => { salidaVoluntaria.current = true; }} />
    </CambiosSinGuardarProvider>
  );
}

function useAjustesDeInput() {
  const location = useLocation();
  useEffect(() => {
    const input = (event: Event) => { document.documentElement.dataset.adsInput = event.type === "keydown" ? "keyboard" : "pointer"; };
    const visibility = () => { document.documentElement.dataset.adsVisibility = document.hidden ? "hidden" : "visible"; };
    visibility();
    document.addEventListener("keydown", input, true);
    document.addEventListener("pointerdown", input, true);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      document.removeEventListener("keydown", input, true);
      document.removeEventListener("pointerdown", input, true);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, []);
  return location;
}

function avisoDeCliente(fault: ClientFault): string {
  return fault === "context-required"
    ? "Recarga la página."
    : "La acción era de otro cliente y no se ha hecho. Comprueba el cliente activo.";
}

/** El marco: barra lateral o cabecera + barra inferior, cinta del cliente, capas y atajos. Solo existe con sesión y clientes. */
function Marco({ me, onSalir }: { me: MeResponse; onSalir: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useAjustesDeInput();
  const activo = useClienteActivo();
  const logout = useLogout();
  const cambiarCliente = useCambioDeCliente();
  const barra = useBarraLateral();
  const [layer, setLayer] = useState<OverlayLayer>(null);
  const [fault, setFault] = useState<ClientFault | null>(null);

  const rol = me.principal.role;
  const esAgencia = rol === "agency";
  const clienteMarco: Client | null = activo.estado === "activo" ? activo.cliente : rol === "store" ? me.clients[0] ?? null : null;
  const businessId = clienteMarco?.business_id ?? "";
  const agenciaBusinessId = esAgencia && activo.estado === "activo" ? activo.businessId : "";
  const slugMarco = clienteMarco?.slug ?? null;

  // Con sesión, la invitación que se estaba aceptando ya se consumió.
  useEffect(() => olvidarInvitacionPendiente(), []);
  useClientAccent(clienteMarco?.accent_color);
  useEffect(() => {
    setActiveClientId(businessId || null);
    return () => setActiveClientId(null);
  }, [businessId]);
  useEffect(() => onClientFault((kind) => {
    setFault(kind);
    if (kind === "mismatch") void queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
  }), [queryClient]);

  const recientes = useRecientes(me.principal.user_id, activo.estado === "activo" ? activo.slug : null);
  const freshness = useFreshness(agenciaBusinessId);
  const killSwitch = useKillSwitch(agenciaBusinessId);
  const setKillSwitch = useSetKillSwitch(agenciaBusinessId);
  const settings = useSettings(agenciaBusinessId);
  useApplyTheme(settings.data?.theme);
  const badgesQuery = useBadges(agenciaBusinessId);
  const chats = useWhatsAppConversations(businessId, "");
  const sinLeer = chats.data?.items.filter((chat) => chat.unread_count > 0).length ?? 0;
  const navBadges = useMemo(() => deriveNavBadges(badgesQuery.data, sinLeer), [badgesQuery.data, sinLeer]);

  const resto = slugMarco ? location.pathname.split(`/c/${encodeURIComponent(slugMarco)}/`)[1] ?? "" : "";
  const seccion = activo.estado === "activo" ? seccionDeRuta(resto) : null;
  const destinoActivo: DestinoId | null = seccion?.destino ?? null;
  const rutaDeAgencia = TITULOS_DE_AGENCIA[location.pathname.replace(/\/+$/, "") || "/"];
  const prefijoSinLeer = sinLeer > 0 ? `(${sinLeer}) ` : "";
  useDocumentTitle(
    seccion && clienteMarco ? `${prefijoSinLeer}${seccion.titulo} · ${clienteMarco.name}`
      : rutaDeAgencia ? `${rutaDeAgencia.titulo} · ${NOMBRE_DE_PRODUCTO}`
      : NOMBRE_DE_PRODUCTO,
  );
  const aviso = useAvisoDeCambioDeCliente(activo.estado === "activo" ? activo.slug : null, activo.cliente?.name ?? null);

  const previoPathname = useRef(location.pathname);
  useEffect(() => {
    const cambioDeCliente = previoPathname.current.split("/")[2] !== location.pathname.split("/")[2];
    previoPathname.current = location.pathname;
    if (!cambioDeCliente && document.documentElement.dataset.adsInput === "keyboard") document.getElementById("main-content")?.focus();
    barra.cerrarSobrepuesta();
    // Solo reacciona a la navegación; `barra` es estable en lo que usa aquí.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleNavigate = useCallback(
    (index: number) => {
      const id = ATAJOS[rol][index];
      if (id && slugMarco) navigate(rutaDeCliente(slugMarco, destinoPorId(id).ruta));
    },
    [navigate, rol, slugMarco],
  );

  const abrirFreno = useCallback(() => setLayer("brake"), []);
  useKeyboardShortcuts({
    onNavigate: handleNavigate,
    onOpenPalette: () => setLayer("palette"),
    onOpenHelp: () => setLayer("help"),
    onOpenBrake: esAgencia ? abrirFreno : undefined,
    onEscape: () => {
      if (setKillSwitch.isPending) return;
      setLayer(null);
      barra.cerrarSobrepuesta();
    },
  });

  function salir() {
    onSalir();
    logout.mutate(undefined, { onSettled: () => navigate("/entrar", { replace: true }) });
  }

  return (
    <div className={styles.shell} data-rol={rol}>
      <div className={styles.cinta} aria-hidden="true" data-testid="cinta-de-cliente" />
      <a href="#main-content" className="skip-link" onClick={(event) => { event.preventDefault(); document.getElementById("main-content")?.focus(); }}>
        Ir al contenido principal
      </a>
      <div className={styles.ranura} data-carril={barra.carrilEnRejilla}>
        <Sidebar
          rol={rol}
          email={me.principal.email}
          cliente={clienteMarco}
          clients={me.clients}
          recientes={recientes}
          onSelectClient={cambiarCliente}
          destinoActivo={destinoActivo}
          destinoDeAgenciaActivo={rutaDeAgencia?.destino ?? null}
          badges={navBadges}
          lagMinutes={freshness.data?.lag_minutes}
          noData={freshness.data?.no_data}
          killSwitch={killSwitch.data}
          onOpenBrake={abrirFreno}
          onOpenHelp={() => setLayer("help")}
          onLogout={salir}
          compacta={barra.soloIconos}
          sobrepuesta={barra.sobrepuesta}
          onToggle={barra.alternar}
          onNavigate={barra.cerrarSobrepuesta}
        />
      </div>
      {barra.sobrepuesta ? <div className={styles.telon} onClick={barra.cerrarSobrepuesta} aria-hidden="true" /> : null}

      <div className={styles.columna}>
        <OfflineBanner />
        {fault ? (
          <div className={styles.fallo} role="alert">
            <span>{avisoDeCliente(fault)}</span>
            {fault === "context-required"
              ? <button type="button" onClick={() => window.location.reload()}>Recargar</button>
              : <button type="button" onClick={() => setFault(null)}>Entendido</button>}
          </div>
        ) : null}
        <MobileHeader
          rol={rol}
          email={me.principal.email}
          cliente={clienteMarco}
          clients={me.clients}
          recientes={recientes}
          onSelectClient={cambiarCliente}
          onOpenSearch={() => setLayer("palette")}
          onLogout={salir}
        />
        {killSwitch.data?.effective.engaged ? (
          <KillSwitchBanner
            engagedAt={killSwitch.data.effective.engaged_at ?? new Date().toISOString()}
            reason={killSwitch.data.effective.reason}
            onRequestRelease={abrirFreno}
          />
        ) : null}
        <main id="main-content" className={styles.main} tabIndex={-1}>
          <Suspense fallback={<PageLoading />}>
            <Outlet />
          </Suspense>
        </main>
        <BottomTabBar rol={rol} cliente={clienteMarco} destinoActivo={destinoActivo} badges={navBadges} onOpenBrake={abrirFreno} />
      </div>

      <div className="visually-hidden" role="status" aria-live="polite">{aviso}</div>
      <UnsavedChangesGuard />

      {layer === "palette" ? (
        <CommandPalette
          rol={rol}
          clients={me.clients}
          cliente={clienteMarco}
          recientes={recientes}
          onSelectClient={cambiarCliente}
          onOpenBrake={abrirFreno}
          onClose={() => setLayer(null)}
        />
      ) : null}
      {layer === "help" ? <ShortcutsHelp rol={rol} onClose={() => setLayer(null)} /> : null}
      {layer === "brake" && esAgencia ? (
        <BrakeDialog
          killSwitch={killSwitch.data}
          onClose={() => setLayer(null)}
          onEngage={async (input) => {
            await setKillSwitch.mutateAsync({ scope_kind: input.scope_kind, scope_id: input.scope_kind === "business" ? agenciaBusinessId : null, mode: input.mode, engaged: true, reason: input.reason });
            setLayer(null);
          }}
          onRelease={async (item, reason) => {
            await setKillSwitch.mutateAsync({
              scope_kind: item.scope_kind,
              scope_id: item.scope_id,
              mode: item.mode,
              engaged: false,
              reason: reason || "Reanudado desde el panel",
              typed_confirmation: "REACTIVAR",
            });
            setLayer(null);
          }}
        />
      ) : null}
    </div>
  );
}
